/**
 * Lead Hunter — schnelle Google-Places-Suche (wie im Agenten-Scout):
 *   1. Alle Suchbegriffe einer Branche (Primär + Ausweich) PARALLEL, je 1 Seite
 *   2. Leads direkt aus den Textsuche-Treffern (Adresse inkl. PLZ) → sofort sichtbar
 *   3. Telefon/Website/Öffnungszeiten danach im Hintergrund (Details-API)
 * Netzwerkzugriff ist injizierbar (`call`), damit Suche und Zuordnung testbar bleiben.
 */
import { getAccessToken } from '@/api/adapters/SupabaseAdapter';
import { BRANCHEN_MAP, BRANCH_SEARCH_CONFIG } from './branches';

const MAX_RETRIES = 3;
const DETAIL_FIELDS = 'formatted_phone_number,website,opening_hours,formatted_address';
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** "Straße Nr, PLZ Stadt, Land" → { strasse, plz, ort } */
export function parseGermanAddress(formatted) {
  const parts = String(formatted || '').split(',').map((s) => s.trim()).filter(Boolean);
  const out = { strasse: parts[0] || '', plz: '', ort: '' };
  const m = (parts[1] || '').match(/^(\d{5})\s+(.+)$/);
  if (m) { out.plz = m[1]; out.ort = m[2]; } else { out.ort = parts[1] || ''; }
  return out;
}

/** Google-Treffer (Text- oder Umkreissuche) → Lead ohne Detailabruf */
export function placeToLead(place, branch, location) {
  let addr = { strasse: '', plz: '', ort: '' };
  if (place.formatted_address) {
    addr = parseGermanAddress(place.formatted_address);
  } else if (place.vicinity) {
    const parts = place.vicinity.split(',').map((s) => s.trim());
    addr = { strasse: parts.length > 1 ? parts.slice(0, -1).join(', ') : '', plz: '', ort: parts[parts.length - 1] || '' };
  }
  return {
    name: place.name,
    ...addr,
    ort: addr.ort || location,
    land: 'Deutschland',
    telefon: '',
    website: '',
    branche: BRANCHEN_MAP[String(branch).toLowerCase()] || branch,
    status: 'Potenzieller Interessent',
    assigned_agent: '',
    google_place_id: place.place_id,
  };
}

/** Details-Antwort → nur vorhandene Felder überschreiben */
export function detailPatch(detail) {
  if (!detail) return {};
  const patch = {};
  if (detail.formatted_phone_number) patch.telefon = detail.formatted_phone_number;
  if (detail.website) patch.website = detail.website;
  if (detail.opening_hours) patch.opening_hours = detail.opening_hours;
  if (detail.formatted_address) {
    const a = parseGermanAddress(detail.formatted_address);
    if (a.strasse) patch.strasse = a.strasse;
    if (a.plz) patch.plz = a.plz;
    if (a.ort) patch.ort = a.ort;
  }
  return patch;
}

/** Alle Suchrunden einer Branche als flache Liste von {keyword, type} (Primär zuerst) */
export function searchQueries(branch) {
  const config = BRANCH_SEARCH_CONFIG[branch];
  if (!config) return [{ keyword: branch, type: null }];
  return [config.primarySearch, ...(config.fallbackSearches || [])]
    .flatMap(({ keywords, type }) => keywords.map((keyword) => ({ keyword, type })));
}

/**
 * Sucht alle Begriffe parallel und liefert eindeutige Treffer (Reihenfolge: Primär zuerst).
 * @param {{ branch: string, location: string, call: Function, errors?: string[] }} opts
 */
export async function searchPlaces({ branch, location, call, errors = [] }) {
  const queries = searchQueries(branch);
  const pages = await Promise.all(queries.map(async ({ keyword, type }) => {
    try {
      const params = { query: `${keyword} in ${location}`, language: 'de', _maxPages: '1' };
      if (type) params.type = type;
      const data = await call('textsearch_paginated', params);
      if (data.status === 'OK') return data.results || [];
      if (data.status && data.status !== 'ZERO_RESULTS') {
        errors.push(`"${keyword}": ${data.status}${data.error_message ? ` — ${data.error_message}` : ''}`);
      }
    } catch (e) {
      errors.push(`"${keyword}": ${e.message || 'unbekannter Fehler'}`);
    }
    return [];
  }));
  const seen = new Set();
  return pages.flat().filter((p) => p.place_id && !seen.has(p.place_id) && seen.add(p.place_id));
}

/** Ort → Mittelpunkt (für den Radius); null wenn nicht auflösbar */
export async function geocodeLocation(location, call) {
  try {
    const data = await call('geocode', { address: location, region: 'de', language: 'de' });
    const loc = data.status === 'OK' ? data.results?.[0]?.geometry?.location : null;
    return loc ? { lat: loc.lat, lng: loc.lng } : null;
  } catch {
    return null;
  }
}

/** Luftlinie in km (Haversine) */
export function distanceKm(a, b) {
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/** Treffer außerhalb des Radius entfernen; ohne Mittelpunkt/Koordinaten bleibt der Treffer */
export function withinRadius(places, center, radiusKm) {
  if (!center || !(radiusKm > 0)) return places;
  return places.filter((p) => {
    const loc = p.geometry?.location;
    return !loc || distanceKm(center, loc) <= radiusKm;
  });
}

/** Details für viele Place-IDs, `batch` gleichzeitig; ruft onBatch({placeId: patch}) je Paket */
export async function loadDetails(placeIds, { call, batch = 6, onBatch, isCancelled = () => false }) {
  const all = {};
  for (let i = 0; i < placeIds.length; i += batch) {
    if (isCancelled()) break;
    const ids = placeIds.slice(i, i + batch);
    const res = await Promise.all(ids.map(async (id) => {
      try {
        const data = await call('details', { place_id: id, fields: DETAIL_FIELDS, language: 'de' });
        return [id, data.status === 'OK' ? detailPatch(data.result) : {}];
      } catch {
        return [id, {}];
      }
    }));
    const patches = Object.fromEntries(res);
    Object.assign(all, patches);
    if (!isCancelled()) onBatch?.(patches, Math.min(i + batch, placeIds.length));
  }
  return all;
}

/** maps-proxy mit Sitzungstoken; 429/5xx/Netzfehler → exponentielles Backoff */
export async function mapsCall(action, params) {
  let lastErr;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const token = await getAccessToken();
      const res = await fetch('/api/maps-proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action, params }),
      });
      if (res.status === 429 || res.status >= 500) {
        const retryAfter = Number(res.headers.get('retry-after'));
        lastErr = new Error(`Maps proxy ${res.status}`);
        if (attempt === MAX_RETRIES) throw lastErr;
        await sleep(retryAfter > 0 ? retryAfter * 1000 : Math.min(8000, 500 * 2 ** attempt));
        continue;
      }
      const data = await res.json();
      if (!res.ok) throw Object.assign(new Error(data.error || `Maps proxy error (${res.status})`), { fatal: true });
      return data;
    } catch (err) {
      lastErr = err;
      if (err.fatal || attempt === MAX_RETRIES) throw err;
      await sleep(Math.min(8000, 500 * 2 ** attempt));
    }
  }
  throw lastErr || new Error('Maps proxy unbekannter Fehler');
}
